import { createApp, nextTick, defineAsyncComponent } from 'vue'
import { createMetaManager } from 'vue-meta'
import VueBrowserUpdate from 'vue-browserupdate'
import PMDApp from './pmd/PMDApp.vue'
import router from './routes'
import store from './store'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/firestore'
import 'firebase/compat/performance'
import 'firebase/compat/messaging'
import 'firebase/compat/storage'
import {
  phoneValidator,
  strongPwdValidator,
  uniqueEmailValidator,
  emailOrPhoneValidator,
  emailValidator,
  twilioPhoneValidator,
  twilioPhoneExcludeHostedValidator,
  handlebarsValidator,
  urlValidator,
  newUrlValidator,
  urlToEmptyValidator,
  notEmptyValidator,
  requiredField,
  confirmedField,
} from './util/validators'
import {
  pythonBackend,
  membershipBackend,
  snapshotBackend,
  snapshotsRevexService,
  dialogflowEngagements,
  paymentService,
  publicApi,
  saasService,
  isvService,
  humanRollOverService,
  interceptorWithBothToken,
  appengineBackend,
  revexBackendService,
  emailBackend,
} from './util/axios_interceptors'
import VueConfetti from 'vue-confetti'
import { getRemoteConfig, fetchAndActivate } from 'firebase/remote-config'
import config from './config'
import axios from 'axios'
import { defineRule } from 'vee-validate'
import { User } from './models'
import NotificationHelper from './util/notification_helper'
import UxMessagePlugin from './plugins/UxMessagePlugin'
import { VueCookieNext } from 'vue-cookie-next'
import LoaderSlot from './pmd/components/LoaderSlot.vue'
import MoonLoaderTitle from './pmd/components/MoonLoaderTitle.vue'
import { VueDraggable } from '@/compatibility/vuedraggable'
import localStorage from 'store'
import { OauthService } from '@/services'
import {
  dispatchCustomEvents,
  refreshFirebaseToken,
  handleExternalRedirect,
  dispatchConversationsV2SyncAll,
  dispatchSyncAlls,
  getHTMLElements,
  setSidebarState,
} from './util/router_helper'
import { translationsDiffChecker } from './localization/utils'
import { fetchCompanyData, resetInactivityTimer, storageEventListener, unhandledRejectionEventListener } from './util/bootstrap_helper'
import { registerNotificationActionHandlers } from './util/notification/NotificationActionService'
import { ensureActiveFirebaseAuthState } from './util/firebase-auth'
import { desktopController } from './util/DesktopController'
import { identifyPendo } from './util/pendo'
import { initializeShellMetadata } from './util/init_shellapp_meta'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'
import { v4 as uuid } from 'uuid'
import pinia from './store/pinia'
import './util/resourceMonitor' // Auto-initializes resource & page load monitoring
import { interactionTrackerPlugin } from './util/interaction-tracker/plugin'

initializeShellMetadata()

// Global vue event bus to transmit/listen event globally
import mitt from 'mitt'
import { Languages, i18n, localizationKey, updateLocale } from './localization'
import { initializeGlobalFilters } from './util/global_filters'
import { initializeGlobalMixins } from './mixins/globalmixin'
import VueObserveVisibility from 'vue-observe-visibility'
import cloneDeep from 'lodash/cloneDeep'
import { vTooltip } from './directives/tooltip/tooltip'
import { between, regex, min, max, confirmed } from '@vee-validate/rules'
const MoonLoader = defineAsyncComponent(() => import('@/pmd/components/MoonLoader.vue'))

try {
  sessionStorage.setItem('sessionId', uuid())
} catch (error) {
  console.error('Error setting sessionId', error)
}

export const app = createApp(PMDApp)

const eventBus = mitt()
app.config.globalProperties.$bus = eventBus
app.component('loader-slot', LoaderSlot)
app.component('moon-loader', MoonLoader)
app.component('moon-loader-title', MoonLoaderTitle)
app.component('vue-draggable', VueDraggable)
app.use(VueConfetti)
app.use(i18n)
app.use(router)
app.use(store)
app.use(VueCookieNext)
app.use(VueObserveVisibility)
app.use(pinia)
app.use(interactionTrackerPlugin, {
  router,
  getters: {
    userId: () => store.state.user?.user?.id,
  },
})
initializeGlobalFilters(app)
initializeGlobalMixins(app)

// Create QueryClient with global config
const queryClient = new QueryClient({})
app.use(VueQueryPlugin, { queryClient })

// import './plugins/setupEmitter'
// array helper
import './util/array_helper'

// tailwindcss

import './assets/pmd/css/tailwind.css'
import { registerComponents } from './pmd/components/UIComponents'
app.use(registerComponents)
// Vue composition API for vue 2
// import VueCompositionAPI from 'vue'
// Vue.use(VueCompositionAPI)
import Notifications from '@kyvg/vue3-notification'
import velocity from 'velocity-animate'
import './util/async_loading_remoteApps'
import './util/runtime_loading_remoteApps'
app.use(Notifications, { velocity })
app.config.globalProperties.devtools = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'staging'
app.config.globalProperties.productionTip = process.env.NODE_ENV === 'development'

app.use(createMetaManager)
app.use(VueBrowserUpdate, {
  options: {},
})

app.use(UxMessagePlugin, {
  rootSelector: '#app',
})

const firebaseApp = firebase.initializeApp(config.firebase)
// Initialize vue-render-inspector (only in non-production environments)
// Must be initialized after firebase app to enable Firestore tracking
import { initializeVueRenderInspector } from './plugins/vue-render-inspector'
initializeVueRenderInspector(app, config)

setupAuth()

if ('serviceWorker' in navigator) {
  let serviceWorkerFilename: string
  if (config.mode === 'dev' || config.mode === 'staging' || config.mode === 'interop') {
    serviceWorkerFilename = '/pmd-fcm-sw-staging.js'
  } else {
    serviceWorkerFilename = '/pmd-fcm-sw.js'
  }
  navigator.serviceWorker
    .register(serviceWorkerFilename)
    .then(registration => {
      NotificationHelper.init(registration)
    })
    .catch(() => {
      // handle catch
    })
}

if (localStorage.get('proxyLoginCount') === undefined) {
  localStorage.set('proxyLoginCount', 0)
}

// eslint-disable-next-line
window.addEventListener('storage', storageEventListener)
// TODO: enable this once we have a way to handle the unhandled rejection
// window.addEventListener('unhandledrejection', unhandledRejectionEventListener)

const activityEvents = ['mousemove', 'keydown', 'click']
activityEvents.forEach(event => window.addEventListener(event, resetInactivityTimer))

firebase.auth().onAuthStateChanged(async user => {
  try {
    // Listener is potentially restoring tokens, skip auth processing for logout route
    const isLogoutRoute = router.currentRoute?.value?.query?.logout === 'true'
    if (isLogoutRoute) {
      return
    }

    // Skip auth processing for support agent login route to prevent API calls with old tokens
    if (router.currentRoute?.value?.name === 'login_support') {
      return
    }

    if (user) {
      const auth = await store.dispatch('auth/get')
      if (auth && auth.userId && !auth.authToken) {
        console.log('JWT not exist')
        const authWithJwt = await OauthService.getCurrentUser()
        if (!authWithJwt?.authToken || !authWithJwt?.refreshToken) {
          throw new Error('Not able to get JWT tokens for the User')
        }
        const authSetData = {
          firebaseToken: authWithJwt.firebaseToken,
          jwt: authWithJwt.jwt,
          refreshJwt: authWithJwt.refreshJwt,
          apiKey: authWithJwt.apiKey,
          userId: authWithJwt.userId,
          companyId: authWithJwt.companyId,
          authToken: authWithJwt.authToken,
          refreshToken: authWithJwt.refreshToken,
        }
        await store.dispatch('auth/set', authSetData)
      } else {
        // if query has app=canny, consider it as a canny app sso login
        const queryParams = new URLSearchParams(window.location.search)
        const cannyCompanyId = queryParams.get('companyID')
        const cannyRedirectUrl = queryParams.get('redirect')
        const isCannyLogin = queryParams.get('app') === 'canny' && cannyCompanyId && cannyRedirectUrl

        if (isCannyLogin && auth.userId) {
          try {
            const response = await OauthService.getCannySSOLoginToken(auth.userId)
            const cannySSOLoginToken = response.cannySSOLoginToken
            // redirect to canny
            const cannySSOUrl = `https://canny.io/api/redirects/sso?ssoToken=${cannySSOLoginToken}&companyID=${cannyCompanyId}&redirect=${cannyRedirectUrl}`
            window.location.assign(cannySSOUrl)
            window.location.href = cannySSOUrl
          } catch (e) {
            console.error('Bootstrap - Error while getting canny sso login token', e)
          }
        }

        try {
          // Handle desktop authentication for already logged-in users
          if (auth.userId) {
            await desktopController.handleDesktopAuthentication()
          }
        } catch (error) {
          console.error('Error while processing desktop auth in onAuthStateChanged', error)
        }
      }
    }
  } catch (error) {
    console.warn('Error while setting tokens', error)
  }
})

import { getDecodedJWT } from './util/jwt_helper'
import { setupAuth } from './util/setup_auth'
import { subAccountRoutes } from '@/routes/constants/agency/subAccounts'

app.config.globalProperties.$private = {}

const remoteConfig = getRemoteConfig(firebaseApp)
remoteConfig.settings.minimumFetchIntervalMillis = 3600000
remoteConfig.settings.fetchTimeoutMillis = 60000
fetchAndActivate(remoteConfig)
  .then(() => {
    console.log('app init')
  })
  .catch(err => {
    console.error('remote config failed: ', err)
  })
app.config.globalProperties.remoteConfig = remoteConfig
app.config.globalProperties.firebaseApp = firebaseApp
axios.defaults.baseURL = config.baseUrl
axios.interceptors.request.use(interceptorWithBothToken)
app.config.globalProperties.$http = axios
app.config.globalProperties.pythonBackend = pythonBackend
app.config.globalProperties.membershipBackend = membershipBackend
app.config.globalProperties.$snapshotBackend = snapshotBackend
app.config.globalProperties.$snapshotsRevexService = snapshotsRevexService
app.config.globalProperties.dialogflowEngagements = dialogflowEngagements
app.config.globalProperties.paymentService = paymentService
app.config.globalProperties.publicApi = publicApi
app.config.globalProperties.saasService = saasService
app.config.globalProperties.isvService = isvService
app.config.globalProperties.revexBackendService = revexBackendService
app.config.globalProperties.humanRollOverService = humanRollOverService
app.config.globalProperties.$appengineBackend = appengineBackend
app.config.globalProperties.emailBackend = emailBackend

// Register the tooltip directive
app.directive('tooltip', vTooltip)

defineRule('phone', phoneValidator)
defineRule('strongPassword', strongPwdValidator)
defineRule('uniqueEmail', uniqueEmailValidator)
defineRule('emailOrPhone', emailOrPhoneValidator)
defineRule('email', emailValidator)
defineRule('twilioPhone', twilioPhoneValidator)
defineRule('twilioPhoneExcludeHosted', twilioPhoneExcludeHostedValidator)
defineRule('required', requiredField)
defineRule('confirmedPassword', confirmedField)
// TODO: run down the line
// app.use(VeeValidate, {
//   classes: true,
//   events: 'input|blur',
//   fieldsBagName: 'veevalidateFields',
// })

defineRule('handlebars', handlebarsValidator)
defineRule('validUrl', urlValidator)
defineRule('newValidUrl', newUrlValidator)
defineRule('validUrlToEmpty', urlToEmptyValidator)
defineRule('notEmpty', notEmptyValidator)
defineRule('regex', regex)
defineRule('between', between)
defineRule('min', min)
defineRule('max', max)
defineRule('confirmed', confirmed)

import { Modules } from './util/modules'
import { configureSentryPageVersion, initializeSentry } from './plugins/sentry'
import modalHub from './plugins/modal-hub'
// import { initDebugging } from './util/performance-debugger/initDebugger'
// import { initializeDatadog } from './plugins/datadog'
import { initializeRequestMetrics } from './plugins/requestMetrics'
import { initMemoryUsage, trackMemoryUsage } from './util/memoryTracking'
import { trackWebVitals } from './util/webVitalsTracking'
import { initNavigationToken } from "./util/initNavigationToken";

initializeSentry(app, router, config, true) // remove in prod
initMemoryUsage(router)
trackWebVitals(router)

// // Initialize Datadog RUM
// initializeDatadog(config)

// // Initialize Request Metrics
initializeRequestMetrics(config)

store.commit('setAgencyLoaderState', true)

// adding modal-hub initialization
modalHub.register()

// initDebugging(router)

// Register notification action handlers for the application
registerNotificationActionHandlers(store)

initNavigationToken(router);

router.beforeEach(async (to, from, next) => {
  // Skip all the logic for logout page, it can create race condition with the auth state change
  const isLogout = to.query?.logout === 'true'
  if (isLogout) {
    return next()
  }

  // Skip all API calls for support agent login route to prevent calls with old tokens
  const unauthenticatedPages = ['login', 'login_sso', 'login_page', 'login_support', 'login_desktop']
  const isUnauthenticatedPage = unauthenticatedPages.includes(to.name || '')
  if (isUnauthenticatedPage) return next()

  // External redirect
  if (to.matched.some(record => record.meta.RedirectExternalUrl)) {
    await handleExternalRedirect(to)
    return
  }
  const fromLocationId = from?.params?.location_id
  let toLocationId = to.params.location_id,
    accountId
  if (to.name === subAccountRoutes.accounts.detail) toLocationId = to.params.account_id
  const currentUser = await store.dispatch('user/get')
  const user = new User(cloneDeep(currentUser))

  await setupLocale(to, from)

  if (toLocationId && user.type === 'account' && user.locations[toLocationId] === undefined) {
    console.log(`you are not authorized to access this location: ${toLocationId}`)
    router.push({ name: 'dashboard' })
    return
  }

  await refreshFirebaseToken(toLocationId)

  // Check if firebase auth instance exists and has proper access to the location
  try {
    await ensureActiveFirebaseAuthState(toLocationId)
  } catch (e) {
    console.error('Bootstrap - Error while checking firebase token', e)
  }

  await dispatchConversationsV2SyncAll(to, from)

  if (fromLocationId !== toLocationId && toLocationId) {
    const eventParams = {
      detail: {
        from: fromLocationId,
        to: toLocationId,
      },
    }
    dispatchCustomEvents('locationChangeEvent', eventParams)

    const [element, htmlTag] = getHTMLElements()
    store.commit('setLocationLoaderState', true)
    try {
      await dispatchSyncAlls(toLocationId, user.id)

      if (element) element.className = ''
      if (htmlTag) htmlTag.className = ''
    } catch (error) {
      console.error('Error while syncing all', error)
    } finally {
      store.commit('setLocationLoaderState', false)
    }
  } else if (accountId !== to.params.account_id) {
    accountId = to.params.account_id
    const [element, htmlTag] = getHTMLElements()
    await refreshFirebaseToken(accountId)
    if (element) element.className = ''
    if (htmlTag) htmlTag.className = ''
  }

  setSidebarState(to, from)

  configureSentryPageVersion(to)

  next()
})

let firstLoadCompleted = false
if (config.googleAnalyticId) {
  window.ga('create', config.googleAnalyticId, 'auto')
  window.ga('set', 'page', router?.currentRoute?.value?.name || '')
  window.ga('send', 'pageview')
}

// Removing Default App Loader...
router.afterEach(async (to, from) => {
  await setupLocale(to, from)

  const fromLocationId = from?.params?.location_id
  let toLocationId = to.params.location_id
  if (fromLocationId !== toLocationId) {
    // location changed or navigated back to agency view
    identifyPendo()
  }

  if (!firstLoadCompleted) {
    nextTick(() => {
      // route is now ready.
      const rootDiv = document.getElementById('app')
      // const htmlTag = document.getElementsByTagName('html')[0]
      if (rootDiv && !['login', 'forgot_password'].includes(to.name || '')) rootDiv.className = ''
      // htmlTag.className = ''
      store.commit('setAgencyLoaderState', false)
      firstLoadCompleted = true
      // console.log('nextTick');
    })
    window.dispatchEvent(
      new CustomEvent('routeLoaded', {
        detail: {
          to: {
            fullPath: to.fullPath,
            name: to.name,
            params: to.params,
            path: to.path,
            query: to.query,
          },
          from: {
            fullPath: from?.fullPath,
            name: from?.name,
            params: from?.params,
            path: from?.path,
            query: from?.query,
          },
        },
      })
    )
  }
  if (config.googleAnalyticId) {
    if (to.name !== from.name) {
      window.ga('set', 'page', to.name)
      window.ga('send', 'pageview')
    }
  }

  if (CustomEvent !== undefined) {
    window.dispatchEvent(
      new CustomEvent('routeChangeEvent', {
        detail: {
          to: {
            fullPath: to.fullPath,
            name: to.name,
            params: to.params,
            path: to.path,
            query: to.query,
          },
          from: {
            fullPath: from.fullPath,
            name: from.name,
            params: from.params,
            path: from.path,
            query: from.query,
          },
        },
      })
    )
  }

  trackMemoryUsage(router, { from: from?.name, to: to?.name })
})

store.subscribe(async (mutation, state) => {
  if (mutation.type === 'company/set') {
    await fetchCompanyData()
  }
})

app.mount('#app')

store.commit('setAgencyLoaderState', true)

export async function setupLocale(to, from) {
  try {
    // List of login-related route names that should maintain their locale until navigation away
    const loginRouteNames = ['login', 'login_sso', 'login_page', 'login_support', 'login_desktop', 'shopify_login', 'eject-login']
    const isLoginRoute = (routeName: string | undefined) => {
      return routeName && loginRouteNames.includes(routeName)
    }

    const isCurrentlyOnLoginRoute = isLoginRoute(to?.name)

    // If we're still on a login route and user data exists, skip locale update to prevent jitter
    // The locale will be updated when navigating away from login to the logged-in screens
    if (isCurrentlyOnLoginRoute && store.state.user?.user) {
      // User data was just loaded while on login screen - skip locale update to prevent jitter
      // The locale will be updated when navigating away from login
      return false
    }

    const userSpecificLanguage = store.state.user?.user?.platformLanguage
    const locationLanguage = store.state.locations?.currentLocation?.locale_settings?.platform
    const localStorageLanguage = window.localStorage.getItem(localizationKey)
    const languagesList = Array.isArray(navigator.languages) ? navigator.languages : []
    const rawBrowserLanguage = (navigator.language || languagesList[0]) as string

    let languageChosen = (function () {
      if (!store.state.user?.user) {
        if (localStorageLanguage) return localStorageLanguage
        if (rawBrowserLanguage) return rawBrowserLanguage
      } else {
        if (userSpecificLanguage) return userSpecificLanguage
        if (locationLanguage) return locationLanguage
      }
      return Languages.English_US
    })()

    const normalizedBrowserLanguage = languageChosen.replace('_', '-').toLowerCase()
    const validLanguages = Object.values(Languages) as string[]
    // Find matching language: first try exact match (case-insensitive), then try 2-letter prefix match
    const matchedLanguage = validLanguages.find(lang => lang.toLowerCase() === normalizedBrowserLanguage) ||
      validLanguages.find(lang => lang.toLowerCase().startsWith(normalizedBrowserLanguage.substring(0, 2)))
    languageChosen = (matchedLanguage as Languages) || Languages.English_US
    if (window.location.search.includes('logout=true')) {
      firstLoadCompleted = false
    }

    // Check the diff
    const translationToLoad = translationsDiffChecker(to, from)
    if (translationToLoad?.length) {
      await updateLocale(languageChosen as Languages, translationToLoad as Modules[])
      return true
    } else if (!firstLoadCompleted) {
      // check for better option
      await updateLocale(languageChosen as Languages, [Modules.Common, Modules.SmartList, Modules.ContactDetail, Modules.Copilot])
    }
  } catch (error) {
    console.error(error)
  }
}
store.$http = axios